import React, { useMemo, useState, useEffect } from 'react';
import { Card, Steps, Button, Row, Col, message, Tag, Modal } from 'antd';
import {
  BasicInfoStep,
  DisposalStep,
  CreditorStep,
  DebtorsStep,
  EvidenceStep,
  ReviewStep,
} from './steps';
import Checklist from './Checklist';
import './DebtPublishWizard.css';
import { DisposalMethod } from '../../../constants/debtEnums';
import { saveDraft, auditProcess, getFormConfig, getDebtDetail } from '../../../services/debt';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { useBreakpoint } from '../../../hooks/useBreakpoint';

/**
 * 债权发布向导
 * 步骤：基本信息 → 处置方式 → 债权人 → 债务人 → 证据材料 → 复核与提交
 * 状态：debtId/debtNo 来自后端保存草稿返回；status 用于控制按钮可用性
 * 校验：前端做关键字段快速校验，详细约束由后端二次校验
 */
const DebtPublishWizard = () => {
  const { isMobile } = useBreakpoint();
  const [current, setCurrent] = useState(0);
  const [debtId, setDebtId] = useState(null); // 草稿保存后后端返回
  const [debtNo, setDebtNo] = useState(null); // 草稿保存后后端返回
  const [formConfig, setFormConfig] = useState(null);
  // 最近一次“保存草稿”的内容哈希，用于发布前检测是否存在未保存更改
  const [lastSavedHash, setLastSavedHash] = useState(null);
  const location = useLocation();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getFormConfig();
        setFormConfig(data);
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('id');
    if (!idParam) return;
    (async () => {
      try {
        const { data } = await getDebtDetail(idParam);
        const { debt, creditor, debtorsPersonal, debtorsEnterprise, evidences } = data || {};
        if (debt?.id) setDebtId(debt.id);
        if (debt?.debtNo) setDebtNo(debt.debtNo);
        setFormData(prev => ({
          ...prev,
          title: debt?.title ?? prev.title,
          debt_type: debt?.debtType ?? prev.debt_type,
          principal_amount: debt?.principalAmount ?? prev.principal_amount,
          interest_amount: debt?.interestAmount ?? prev.interest_amount,
          penalty_amount: debt?.penaltyAmount ?? prev.penalty_amount,
          other_amount: debt?.otherAmount ?? prev.other_amount,
          total_amount: debt?.totalAmount ?? prev.total_amount,
          low_price_option: debt?.lowPriceOption ?? prev.low_price_option,
          debt_occurrence_date: debt?.debtOccurrenceDate ? dayjs(debt.debtOccurrenceDate) : prev.debt_occurrence_date,
          description: debt?.description ?? prev.description,
          is_anonymous: (debt?.isAnonymous ?? prev.is_anonymous) === 1,
          is_original_creditor: (debt?.isOriginalCreditor ?? (prev.is_original_creditor ? 1 : 0)) === 1,
          disposal_method: debt?.disposalMethod ?? prev.disposal_method,
          transfer_pricing_amount: debt?.transferPricingAmount ?? prev.transfer_pricing_amount,
          transfer_discount_rate: debt?.transferDiscountRate ?? prev.transfer_discount_rate,
          creditor: creditor ? {
            creditor_type: creditor.creditorType,
            name: creditor.name,
            id_card_no: creditor.idCardNo,
            company_name: creditor.companyName,
            social_credit_code: creditor.socialCreditCode,
            legal_person_name: creditor.legalPersonName,
            legal_person_id_no: creditor.legalPersonIdNo,
            phone: creditor.phone,
            email: creditor.email,
            province: creditor.province,
            city: creditor.city,
            address: creditor.address,
          } : prev.creditor,
          debtorsPersonal: Array.isArray(debtorsPersonal) ? debtorsPersonal.map(d => ({
            name: d.name,
            id_type: d.idType,
            id_no: d.idNo,
            phone: d.phone,
            email: d.email,
            province: d.province,
            city: d.city,
            address: d.address,
            id_card_front_url: d.idCardFrontUrl,
            id_card_back_url: d.idCardBackUrl,
            other_id_urls: d.otherIdUrls,
            non_performance_reason: d.nonPerformanceReason,
          })) : prev.debtorsPersonal,
          debtorsEnterprise: Array.isArray(debtorsEnterprise) ? debtorsEnterprise.map(e => ({
            company_name: e.companyName,
            social_credit_code: e.socialCreditCode,
            legal_person_name: e.legalPersonName,
            legal_person_id_no: e.legalPersonIdNo,
            phone: e.phone,
            email: e.email,
            province: e.province,
            city: e.city,
            address: e.address,
            non_performance_reason: e.nonPerformanceReason,
          })) : prev.debtorsEnterprise,
          evidences: Array.isArray(evidences) ? evidences.map(ev => ({
            file_url: ev.fileUrl,
            evidence_type: ev.evidenceType,
            remark: ev.remark,
          })) : prev.evidences,
          status: debt?.status ?? prev.status,
          collection_status: debt?.collectionStatus ?? prev.collection_status,
        }));
      } catch (e) {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    // 当草稿ID就绪时，尝试从 localStorage 读取对应哈希（草稿保存时写入）
    if (debtId) {
      const k = `debt:lastSavedHash:${debtId}`;
      const v = localStorage.getItem(k) || null;
      setLastSavedHash(v);
    }
  }, [debtId]);

  // 统一的表单数据容器
  const [formData, setFormData] = useState({
    // 基本信息
    title: '',
    debt_type: undefined,
    principal_amount: 0,
    interest_amount: 0,
    penalty_amount: 0,
    other_amount: 0,
    total_amount: 0,
    debt_occurrence_date: null,
    description: '',
    is_anonymous: false,
    is_original_creditor: true,
    // 处置方式
    disposal_method: DisposalMethod.RETAIN,
    low_price_option: undefined,
    transfer_pricing_amount: undefined,
    transfer_discount_rate: undefined,
    // 债权人
    creditor: {
      creditor_type: 'P',
      name: '',
      id_card_no: '',
      company_name: '',
      social_credit_code: '',
      legal_person_name: '',
      legal_person_id_no: '',
      phone: '',
      email: '',
      province: '',
      city: '',
      address: '',
    },
    // 债务人列表
    debtorsPersonal: [],
    debtorsEnterprise: [],
    // 证据材料
    evidences: [],
    status: 'DRAFT',
    collection_status: 'NOT_COLLECTED',
  });

  // 计算总涉及金额（只读，整数）：本金 + 违约金 + 其他，不包含利息
  const totalAmount = useMemo(() => {
    const p = parseInt(formData.principal_amount || 0, 10) || 0;
    const pe = parseInt(formData.penalty_amount || 0, 10) || 0;
    const o = parseInt(formData.other_amount || 0, 10) || 0;
    return p + pe + o;
  }, [formData.principal_amount, formData.penalty_amount, formData.other_amount]);

  // 将计算值同步到数据
  const syncTotalAmount = () => {
    setFormData(prev => ({ ...prev, total_amount: Number(totalAmount) }));
  };

  const steps = [
    { title: '基本信息' },
    { title: '处置方式' },
    { title: '债权人' },
    { title: '债务人' },
    { title: '证据材料' },
    { title: '复核与提交' },
  ];

  const handleNext = () => {
    syncTotalAmount();
    setCurrent((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrev = () => {
    setCurrent((prev) => Math.max(prev - 1, 0));
  };

  const handleSaveDraft = async () => {
    syncTotalAmount();
    try {
      const payload = { ...formData, id: debtId };
      payload.debt_occurrence_date = formData.debt_occurrence_date ? dayjs(formData.debt_occurrence_date).format('YYYY-MM-DD') : null;
      payload.evidences = Array.isArray(payload.evidences) ? payload.evidences.filter(it => it && it.file_url) : [];
      const res = await saveDraft(payload);
      const { data } = res || {};
      if (data?.debtNo) {
        setDebtNo(data.debtNo);
        localStorage.setItem('debtDraftNo', data.debtNo);
      }
      if (data?.id) {
        setDebtId(data.id);
        localStorage.setItem('debtDraftId', String(data.id));
        try {
          const detailRes = await getDebtDetail(data.id);
          const det = detailRes?.data;
          if (det?.evidences) {
            setFormData(prev => ({
              ...prev,
              evidences: Array.isArray(det.evidences) ? det.evidences.map(ev => ({
                file_url: ev.fileUrl,
                evidence_type: ev.evidenceType,
                remark: ev.remark,
              })) : prev.evidences,
            }));
          }
        } catch (_) {}
        // 生成稳定序列化字符串并计算哈希；仅包含会影响发布的业务字段
        const s = stableStringify({
          title: formData.title,
          debt_type: formData.debt_type,
          principal_amount: Number(formData.principal_amount || 0),
          interest_amount: Number(formData.interest_amount || 0),
          penalty_amount: Number(formData.penalty_amount || 0),
          other_amount: Number(formData.other_amount || 0),
          total_amount: Number(formData.total_amount || 0),
          debt_occurrence_date: payload.debt_occurrence_date || null,
          description: formData.description,
          is_anonymous: !!formData.is_anonymous,
          is_original_creditor: !!formData.is_original_creditor,
          disposal_method: formData.disposal_method,
          low_price_option: formData.low_price_option,
          transfer_pricing_amount: formData.transfer_pricing_amount,
          transfer_discount_rate: formData.transfer_discount_rate,
          creditor: { ...formData.creditor },
          debtorsPersonal: Array.isArray(formData.debtorsPersonal) ? formData.debtorsPersonal : [],
          debtorsEnterprise: Array.isArray(formData.debtorsEnterprise) ? formData.debtorsEnterprise : [],
          evidences: Array.isArray(payload.evidences) ? payload.evidences : [],
        });
        // 保存哈希到状态与 localStorage，供“发布”时比对
        const h = hashString(s);
        setLastSavedHash(h);
        localStorage.setItem(`debt:lastSavedHash:${data.id}`, h);
      }
      setFormData(prev => ({ ...prev, status: 'DRAFT' }));
      message.success('草稿已保存');
    } catch (e) {
      message.warning('草稿保存接口暂不可用，已模拟保存');
      if (!debtNo) {
        setDebtNo('D202501010001');
        localStorage.setItem('debtDraftNo', 'D202501010001');
      }
      if (!debtId) {
        setDebtId(1);
        localStorage.setItem('debtDraftId', '1');
      }
      const payload = { ...formData };
      payload.debt_occurrence_date = formData.debt_occurrence_date ? dayjs(formData.debt_occurrence_date).format('YYYY-MM-DD') : null;
      payload.evidences = Array.isArray(payload.evidences) ? payload.evidences.filter(it => it && it.file_url) : [];
      // 模拟保存路径同样生成哈希，保持行为一致
      const s = stableStringify({
        title: formData.title,
        debt_type: formData.debt_type,
        principal_amount: Number(formData.principal_amount || 0),
        interest_amount: Number(formData.interest_amount || 0),
        penalty_amount: Number(formData.penalty_amount || 0),
        other_amount: Number(formData.other_amount || 0),
        total_amount: Number(formData.total_amount || 0),
        debt_occurrence_date: payload.debt_occurrence_date || null,
        description: formData.description,
        is_anonymous: !!formData.is_anonymous,
        is_original_creditor: !!formData.is_original_creditor,
        disposal_method: formData.disposal_method,
        low_price_option: formData.low_price_option,
        transfer_pricing_amount: formData.transfer_pricing_amount,
        transfer_discount_rate: formData.transfer_discount_rate,
        creditor: { ...formData.creditor },
        debtorsPersonal: Array.isArray(formData.debtorsPersonal) ? formData.debtorsPersonal : [],
        debtorsEnterprise: Array.isArray(formData.debtorsEnterprise) ? formData.debtorsEnterprise : [],
        evidences: Array.isArray(payload.evidences) ? payload.evidences : [],
      });
      const h = hashString(s);
      setLastSavedHash(h);
      const idStored = localStorage.getItem('debtDraftId');
      if (idStored) localStorage.setItem(`debt:lastSavedHash:${idStored}`, h);
      setFormData(prev => ({ ...prev, status: 'DRAFT' }));
    }
  };

  // 提交发布：前置校验关键字段与低价转让约束
  const handleSubmit = async () => {
    syncTotalAmount();
    // 前端合规性快速校验（关键字段）
    if (!formData.title || !formData.debt_type) {
      message.error('请填写标题与债权类型');
      return;
    }
    if (!formData.principal_amount || Number(formData.principal_amount) <= 0) {
      message.error('本金金额必须大于0');
      return;
    }
    if (!formData.debt_occurrence_date) {
      message.error('请填写债权发生日');
      return;
    }
    // 处置方式为低价转让时校验
    if (formData.disposal_method === DisposalMethod.LOW_PRICE_TRANSFER) {
      const total = Number(formData.total_amount || 0) || 0;
      const limit = total * 0.30;
      const opt = String(formData.low_price_option || '');
      if (!opt) {
        message.error('请选择低价转让方式');
        return;
      }
      if (opt === 'PRICING') {
        const v = Number(formData.transfer_pricing_amount || 0);
        if (!Number.isFinite(v)) {
          message.error('请填写有效的定价金额');
          return;
        }
        if (v < 0) {
          message.error('定价金额不得小于0');
          return;
        }
        if (v > limit) {
          message.error('定价金额不得超过总金额的30%');
          return;
        }
        if (formData.transfer_discount_rate) {
          message.error('已选择定价，折扣不应填写');
          return;
        }
      } else if (opt === 'DISCOUNT') {
        const r = Number(formData.transfer_discount_rate);
        if (!Number.isFinite(r)) {
          message.error('请填写有效的折扣比例');
          return;
        }
        if (r < 0 || r > 100) {
          message.error('折扣比例需在0-100之间');
          return;
        }
        if (r > 30) {
          message.error('折扣比例不得超过30%');
          return;
        }
        if (formData.transfer_pricing_amount) {
          message.error('已选择折扣，定价不应填写');
          return;
        }
      } else if (opt === 'NEGOTIATED') {
        // 无需金额或折扣（另行商议）
      }
    }
    // 债权人基本信息
    // 债权人信息：个人需姓名+身份证，企业需名称+社会信用代码
    if (formData.creditor.creditor_type === 'P') {
      if (!formData.creditor.name || !formData.creditor.id_card_no) {
        message.error('请填写债权人个人姓名与身份证号');
        return;
      }
    } else {
      if (!formData.creditor.company_name || !formData.creditor.social_credit_code) {
        message.error('请填写债权人企业名称与统一社会信用代码');
        return;
      }
    }

    // 构造当前屏幕上的内容快照（稳定序列化），用于与上次保存的哈希进行比较
    const buildCurrentString = () => {
      const payload = { ...formData };
      payload.debt_occurrence_date = formData.debt_occurrence_date ? dayjs(formData.debt_occurrence_date).format('YYYY-MM-DD') : null;
      payload.evidences = Array.isArray(payload.evidences) ? payload.evidences.filter(it => it && it.file_url) : [];
      return stableStringify({
        title: formData.title,
        debt_type: formData.debt_type,
        principal_amount: Number(formData.principal_amount || 0),
        interest_amount: Number(formData.interest_amount || 0),
        penalty_amount: Number(formData.penalty_amount || 0),
        other_amount: Number(formData.other_amount || 0),
        total_amount: Number(formData.total_amount || 0),
        debt_occurrence_date: payload.debt_occurrence_date || null,
        description: formData.description,
        is_anonymous: !!formData.is_anonymous,
        is_original_creditor: !!formData.is_original_creditor,
        disposal_method: formData.disposal_method,
        low_price_option: formData.low_price_option,
        transfer_pricing_amount: formData.transfer_pricing_amount,
        transfer_discount_rate: formData.transfer_discount_rate,
        creditor: { ...formData.creditor },
        debtorsPersonal: Array.isArray(formData.debtorsPersonal) ? formData.debtorsPersonal : [],
        debtorsEnterprise: Array.isArray(formData.debtorsEnterprise) ? formData.debtorsEnterprise : [],
        evidences: Array.isArray(payload.evidences) ? payload.evidences : [],
      });
    };
    // 计算当前内容哈希
    const currentHash = hashString(buildCurrentString());
    // 真正执行发布：确保存在草稿ID并调用审核发布接口
    const proceed = async () => {
      try {
        const id = await ensureDebtId();
        await auditProcess(id);
        setFormData(prev => ({ ...prev, status: 'PUBLISHED' }));
        message.success('债权发布成功');
      } catch (e) {
        // 使用后端返回的实际错误消息，给用户准确的反馈
        const errorMsg = e.message || e.response?.data?.message || '发布失败，请稍后重试';
        message.error(errorMsg);
        throw e; // 向上抛出，避免继续执行后续逻辑
      }
    };
    if (!lastSavedHash || lastSavedHash !== currentHash) {
      // 检测到未保存修改：弹窗确认，用户同意后自动保存草稿再发布
      Modal.confirm({
        title: '检测到未保存的修改',
        content: '是否先保存草稿再发布？',
        onOk: async () => {
          await handleSaveDraft();
          await proceed();
        },
      });
      return;
    }
    await proceed();
  };

  // 确保存在债权ID：没有则先保存草稿获取ID
  const ensureDebtId = async () => {
    if (debtId) return debtId;
    try {
      const payload = { ...formData, id: null };
      payload.debt_occurrence_date = formData.debt_occurrence_date ? dayjs(formData.debt_occurrence_date).format('YYYY-MM-DD') : null;
      const res = await saveDraft(payload);
      const { data } = res || {};
      if (data?.debtNo) {
        setDebtNo(data.debtNo);
        localStorage.setItem('debtDraftNo', data.debtNo);
      }
      if (data?.id) {
        setDebtId(data.id);
        localStorage.setItem('debtDraftId', String(data.id));
        // 首次保存草稿后也生成哈希并落盘，保证发布比较有参照
        const s = stableStringify({
          title: formData.title,
          debt_type: formData.debt_type,
          principal_amount: Number(formData.principal_amount || 0),
          interest_amount: Number(formData.interest_amount || 0),
          penalty_amount: Number(formData.penalty_amount || 0),
          other_amount: Number(formData.other_amount || 0),
          total_amount: Number(formData.total_amount || 0),
          debt_occurrence_date: payload.debt_occurrence_date || null,
          description: formData.description,
          is_anonymous: !!formData.is_anonymous,
          is_original_creditor: !!formData.is_original_creditor,
          disposal_method: formData.disposal_method,
          low_price_option: formData.low_price_option,
          transfer_pricing_amount: formData.transfer_pricing_amount,
          transfer_discount_rate: formData.transfer_discount_rate,
          creditor: { ...formData.creditor },
          debtorsPersonal: Array.isArray(formData.debtorsPersonal) ? formData.debtorsPersonal : [],
          debtorsEnterprise: Array.isArray(formData.debtorsEnterprise) ? formData.debtorsEnterprise : [],
          evidences: Array.isArray(payload.evidences) ? payload.evidences : [],
        });
        const h = hashString(s);
        setLastSavedHash(h);
        localStorage.setItem(`debt:lastSavedHash:${data.id}`, h);
        return data.id;
      }
      const cached = Number(localStorage.getItem('debtDraftId')) || null;
      return cached;
    } catch (e) {
      // 使用后端返回的实际错误消息
      const errorMsg = e.message || e.response?.data?.message || '保存草稿失败，请稍后重试';
      message.error(errorMsg);
      throw e;
    }
  };

  // 简易字符串散列（非安全），仅用于前端差异检测；避免引入额外库
  const hashString = (str) => {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    return String(h >>> 0);
  };

  // 稳定序列化：对象键排序、日期/数字/布尔规范化，保证同内容生成同字符串
  const stableStringify = (value) => {
    const type = Object.prototype.toString.call(value);
    if (type === '[object Array]') {
      return '[' + value.map((v) => stableStringify(v)).join(',') + ']';
    }
    if (type === '[object Object]') {
      const keys = Object.keys(value).sort();
      return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
    }
    if (type === '[object Date]') {
      return JSON.stringify(dayjs(value).format('YYYY-MM-DD'));
    }
    if (type === '[object String]') {
      return JSON.stringify(String(value));
    }
    if (type === '[object Number]') {
      return JSON.stringify(Number(value));
    }
    if (type === '[object Boolean]') {
      return JSON.stringify(!!value);
    }
    if (value === null || value === undefined) return 'null';
    return JSON.stringify(value);
  };

  return (
    <div className="debt-publish-wizard space-y-md">
      <div className="wizard-header">
        <div className="wizard-title">
          <div className="page-title wizard-page-title">发布债权</div>
          {debtNo && (
            <span className="debt-no">编号：{debtNo}</span>
          )}
          <Tag
            className="wizard-status-tag"
            color={formData.status === 'PENDING' ? 'orange' : formData.status === 'PUBLISHED' ? 'green' : 'blue'}
          >
            {formData.status === 'PENDING' ? '待审核' : formData.status === 'PUBLISHED' ? '已发布' : '草稿'}
          </Tag>
        </div>
        <div className="wizard-actions">
          <Button className="btn-compact" onClick={handleSaveDraft} disabled={formData.status !== 'DRAFT'}>保存草稿</Button>
          <Button className="btn-compact" type="primary" onClick={handleSubmit} disabled={formData.status !== 'DRAFT'}>发布债权</Button>
        </div>
      </div>

      <Card className="card-compact wizard-card">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            {isMobile ? (
              <div className="c-steps-mobile-header" aria-label="步骤信息">
                <div className="c-steps-mobile-left">
                  <span className="c-steps-mobile-stepno">{`第 ${current + 1} 步`}</span>
                  <span className="c-steps-mobile-title">{steps[current]?.title || ''}</span>
                </div>
                <div className="c-steps-mobile-progress">{`${current + 1}/${steps.length}`}</div>
              </div>
            ) : (
              <Steps current={current} responsive>
                {steps.map(s => <Steps.Step key={s.title} title={s.title} />)}
              </Steps>
            )}
            <div className="wizard-content">
              {current === 0 && (
                <BasicInfoStep
                  value={formData}
                  onChange={setFormData}
                  totalAmount={totalAmount}
                  onNext={handleNext}
                  config={formConfig}
                />
              )}
              {current === 1 && (
                <DisposalStep
                  value={formData}
                  onChange={setFormData}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  config={formConfig}
                />
              )}
              {current === 2 && (
                <CreditorStep
                  value={formData}
                  onChange={setFormData}
                  onNext={handleNext}
                  onPrev={handlePrev}
                />
              )}
              {current === 3 && (
                <DebtorsStep
                  value={formData}
                  onChange={setFormData}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  debtId={debtId}
                  ensureDebtId={ensureDebtId}
                />
              )}
              {current === 4 && (
                <EvidenceStep
                  value={formData}
                  onChange={setFormData}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  debtId={debtId}
                  ensureDebtId={ensureDebtId}
                />
              )}
              {current === 5 && (
                <ReviewStep
                  value={formData}
                  onPrev={handlePrev}
                  onSubmit={handleSubmit}
                  onSaveDraft={handleSaveDraft}
                  onSubmitPublish={handleSubmit}
                  config={formConfig}
                />
              )}
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <Checklist value={formData} current={current} />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default DebtPublishWizard;
